import { createContext, useContext, useReducer, useCallback } from 'react';
import { EMPTY_DOC } from '../core/types.js';
import {
  insertBlock as opInsert,
  removeBlock as opRemove,
  moveBlock as opMove,
  updateAttrs as opUpdateAttrs,
  duplicateBlock as opDuplicate,
} from '../core/operations.js';
import { normalizeDoc } from '../core/normalize.js';
import {
  createHistory,
  pushHistory,
  undo as histUndo,
  redo as histRedo,
  canUndo,
  canRedo,
} from '../core/history.js';

// ─── Contexts ──────────────────────────────────────────────────────────────
export const EditorDispatchContext = createContext(null);
export const EditorStateContext = createContext(null);

// ─── Initial state ──────────────────────────────────────────────────────────
const buildInitial = (doc = EMPTY_DOC) => ({
  // Doc history (past/present/future)
  history: createHistory(normalizeDoc(doc)),

  // UI state (ephemeral)
  selectedId: null,
  dragState: {
    activeId: null,
    overContainerId: null,
    indicatorIndex: -1,
  },
  clipboard: null,
});

// ─── Helpers ────────────────────────────────────────────────────────────────
const withDoc = (state, nextDoc) => ({
  ...state,
  history: pushHistory(state.history, nextDoc),
});

// ─── Reducer ────────────────────────────────────────────────────────────────
function editorReducer(state, action) {
  const doc = state.history.present;

  switch (action.type) {
    // Doc mutations
    case 'INSERT_BLOCK': {
      const { parentId, index, blockType, initialAttrs } = action;
      const next = opInsert(doc, parentId, index, blockType, initialAttrs);
      return withDoc(state, next);
    }
    case 'REMOVE_BLOCK': {
      const next = opRemove(doc, action.id);
      const selectedId = state.selectedId === action.id ? null : state.selectedId;
      return { ...withDoc(state, next), selectedId };
    }
    case 'MOVE_BLOCK': {
      const { id, toParentId, toIndex } = action;
      const next = opMove(doc, id, toParentId, toIndex);
      return withDoc(state, next);
    }
    case 'UPDATE_ATTRS': {
      const next = opUpdateAttrs(doc, action.id, action.patch);
      return withDoc(state, next);
    }
    case 'DUPLICATE_BLOCK': {
      const { doc: next, newId } = opDuplicate(doc, action.id);
      return { ...withDoc(state, next), selectedId: newId };
    }
    case 'NORMALIZE_DOC': {
      return withDoc(state, normalizeDoc(doc));
    }
    case 'REPLACE_DOC': {
      return {
        ...buildInitial(action.doc),
        selectedId: null,
      };
    }

    // History
    case 'UNDO': {
      if (!canUndo(state.history)) return state;
      return { ...state, history: histUndo(state.history) };
    }
    case 'REDO': {
      if (!canRedo(state.history)) return state;
      return { ...state, history: histRedo(state.history) };
    }

    // UI state (no history push)
    case 'SELECT_BLOCK':
      return { ...state, selectedId: action.id };
    case 'DESELECT':
      return { ...state, selectedId: null };
    case 'SET_DRAG_STATE':
      return { ...state, dragState: { ...state.dragState, ...action.dragState } };
    case 'CLEAR_DRAG_STATE':
      return { ...state, dragState: { activeId: null, overContainerId: null, indicatorIndex: -1 } };
    case 'COPY_BLOCK': {
      const block = doc.blocks[action.id];
      return block ? { ...state, clipboard: block } : state;
    }

    default:
      return state;
  }
}

// ─── Provider ────────────────────────────────────────────────────────────────
export function EditorStoreProvider({ initialDoc, children }) {
  const [state, dispatch] = useReducer(editorReducer, buildInitial(initialDoc));
  return (
    <EditorDispatchContext.Provider value={dispatch}>
      <EditorStateContext.Provider value={state}>
        {children}
      </EditorStateContext.Provider>
    </EditorDispatchContext.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────
export function useEditorState() {
  return useContext(EditorStateContext);
}

export function useEditorDispatch() {
  return useContext(EditorDispatchContext);
}

/** Convenience hook: returns the live doc. */
export function useDoc() {
  return useEditorState().history.present;
}

/** Convenience hook: returns the selected block id. */
export function useSelectedId() {
  return useEditorState().selectedId;
}

/** Convenience hook: returns { canUndo, canRedo }. */
export function useHistoryInfo() {
  const { history } = useEditorState();
  return { canUndo: canUndo(history), canRedo: canRedo(history) };
}

/** Convenience hook: returns stable action dispatchers. */
export function useEditorActions() {
  const dispatch = useEditorDispatch();

  return {
    insertBlock: useCallback(
      (parentId, index, blockType, initialAttrs) =>
        dispatch({ type: 'INSERT_BLOCK', parentId, index, blockType, initialAttrs }),
      [dispatch],
    ),
    removeBlock: useCallback(
      (id) => dispatch({ type: 'REMOVE_BLOCK', id }),
      [dispatch],
    ),
    moveBlock: useCallback(
      (id, toParentId, toIndex) => dispatch({ type: 'MOVE_BLOCK', id, toParentId, toIndex }),
      [dispatch],
    ),
    updateAttrs: useCallback(
      (id, patch) => dispatch({ type: 'UPDATE_ATTRS', id, patch }),
      [dispatch],
    ),
    duplicateBlock: useCallback(
      (id) => dispatch({ type: 'DUPLICATE_BLOCK', id }),
      [dispatch],
    ),
    selectBlock: useCallback(
      (id) => dispatch({ type: 'SELECT_BLOCK', id }),
      [dispatch],
    ),
    deselect: useCallback(
      () => dispatch({ type: 'DESELECT' }),
      [dispatch],
    ),
    undo: useCallback(() => dispatch({ type: 'UNDO' }), [dispatch]),
    redo: useCallback(() => dispatch({ type: 'REDO' }), [dispatch]),
    replaceDoc: useCallback(
      (doc) => dispatch({ type: 'REPLACE_DOC', doc }),
      [dispatch],
    ),
    copyBlock: useCallback(
      (id) => dispatch({ type: 'COPY_BLOCK', id }),
      [dispatch],
    ),
    setDragState: useCallback(
      (dragState) => dispatch({ type: 'SET_DRAG_STATE', dragState }),
      [dispatch],
    ),
    clearDragState: useCallback(
      () => dispatch({ type: 'CLEAR_DRAG_STATE' }),
      [dispatch],
    ),
  };
}
